import { giphyAqua, giphyBlue, giphyDarkCharcoal, giphyGreen, giphyRed } from '@giphy/colors'
import { useCallback, useContext, useState } from 'react'
import ReactCodeInput from 'react-code-input'
import styled, { css } from 'styled-components'
import { FlashMessageContext } from '../../context/flash-message'
import { NexaBlack } from '../../styles/fonts'
import Background from './background'
import { Button as _Button } from './sc'
import { desktop } from 'ui/src/css'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { timeoutFetch } from 'utils/src/api/fetch'

type EmailValidationProps = {
    copy?: EmailValidationCopy
    email: string
    loginHref?: string
    onVerified?: (() => void) | undefined
}

export type EmailValidationCopy = {
    alreadyValidated: string
    fallbackEmail: string
    footerLineOne: string
    footerLineTwo: string
    goToLogin: string
    goToLoginAriaLabel: string
    invalidCode: string
    messagePrefix: string
    messageSuffix: string
    submit: string
    title: string
    verifiedMessage: string
}

export const defaultEmailValidationCopy: EmailValidationCopy = {
    alreadyValidated: 'Already validated your account?!',
    fallbackEmail: 'test@email.com',
    footerLineOne: "Didn't receive an email? Check your spam folder!",
    footerLineTwo: 'Emails may take a couple minutes to arrive.',
    goToLogin: 'Go to Log In',
    goToLoginAriaLabel: 'Go to Log In',
    invalidCode: 'Invalid Code',
    messagePrefix: 'We have sent an email to ',
    messageSuffix:
        '. Please click the link in the email to verify your account or enter the email activation code below.',
    submit: 'Submit',
    title: 'Check your Email!',
    verifiedMessage: 'Email Verified!',
}

const Title = styled.div`
    font-family: ${NexaBlack.style.fontFamily};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-size: 28px;
    margin-bottom: 16px;
`

const Message = styled.div`
    font-size: 18px;
    font-weight: normal;
    text-align: center;
    letter-spacing: 0px;
    line-height: 22px;
`

const Footer = styled.div`
    font-size: 15px;
    font-weight: normal;
    text-align: center;
    letter-spacing: 0.11px;
`
const ResendLink = styled.div`
    font-weight: bold;
    cursor: pointer;
`
const AuthSwitchButton = styled.button`
    font-weight: bold;
    cursor: pointer;
    background: transparent;
    border: 0;
    padding: 0;
`
const Email = styled.span`
    color: ${giphyBlue};
    font-weight: bold;
`
const Error = styled.p`
    color: ${giphyRed};
    font-size: 15px;
    font-weight: normal;
    font-style: italic;
    text-align: center;
    letter-spacing: 0.11px;
`
const Container = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0);
    display: flex;
    align-items: center;
    ${desktop(css`
        width: 50vw;
    `)}
`
const Inner = styled.div`
    width: 332px;
    flex-direction: column;
    display: flex;
    gap: 10px;
    margin: auto;
    align-items: center;
    text-align: center;
    position: relative;
`

const Button = styled(_Button)`
    width: 100%;
`
export const ReactCodeInputWrapper = styled.div<{ $error: any }>`
    margin-bottom: 20px;

    input {
        width: 52px;
        height: 52px;
        margin: 8px;
        border-radius: 4px;
        background: ${giphyDarkCharcoal};
        font-size: 48px;
        text-align: center;
        color: ${giphyAqua};
        font-size: 25px;
        font-family: ${NexaBlack.style.fontFamily};
        text-align: center;
        letter-spacing: 0.38px;
        line-height: 35px;
        border-width: 0;
        outline-width: 0;
        box-sizing: border-box;

        ${({ $error }) => {
            return $error
                ? `
      border-width: 2px;
      border-style: solid;
      border-color: ${giphyRed};
      color: ${giphyRed};
    `
                : ``
        }}

        &:focus {
            border-width: 2px;
            border-style: solid;
            border-color: ${giphyGreen};
        }
    }
`
const EmailValidation = ({
    copy = defaultEmailValidationCopy,
    email,
    loginHref = '/login',
    onVerified,
}: EmailValidationProps) => {
    const [code, setCode] = useState('')
    const router = useRouter()
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(false)

    const { showMessage } = useContext(FlashMessageContext)

    const codeChangedCallback = (value: string) => {
        setCode(value)
        setError(false)
    }

    const submitCode = useCallback(() => {
        setSubmitting(true)

        const submit = async () => {
            try {
                const response = await timeoutFetch({
                    url: `/api/v3/register/confirm`,
                    options: {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            email,
                            code,
                        }),
                    },
                })
                if (response.ok) {
                    showMessage({ message: copy.verifiedMessage, type: 'success' })
                    if (onVerified) {
                        onVerified()
                    } else {
                        router.push('/login')
                    }
                } else {
                    const err = await response.json()
                    if (response.status === 410) {
                        showMessage({ message: err.details.code || 'error', type: 'error' })
                    } else {
                        setError(true)
                    }
                }
            } catch (err: any) {
                setError(true)
            } finally {
                setSubmitting(false)
            }
        }
        submit()
    }, [copy.verifiedMessage, email, code, onVerified, showMessage, router])

    return (
        <Container>
            <Background />
            <Inner>
                {/*  eslint-disable-next-line @next/next/no-img-element */}
                <img
                    src="https://media2.giphy.com/media/LOnt6uqjD9OexmQJRB/200w.webp"
                    width={100}
                    height={100}
                    alt=""
                />
                <Title>{copy.title}</Title>
                <Message>
                    {copy.messagePrefix}
                    <Email>{email || copy.fallbackEmail}</Email>
                    {copy.messageSuffix}
                </Message>
                <ReactCodeInputWrapper $error={error}>
                    <ReactCodeInput
                        name="input-code"
                        inputMode="verbatim"
                        fields={4}
                        type="text"
                        onChange={codeChangedCallback}
                        value={code}
                    />
                </ReactCodeInputWrapper>
                {error && <Error>{copy.invalidCode}</Error>}
                <Footer>
                    {copy.footerLineOne} <br />
                    {copy.footerLineTwo}
                </Footer>
                <Footer>{copy.alreadyValidated}</Footer>
                {onVerified ? (
                    <AuthSwitchButton onClick={onVerified} type="button" aria-label={copy.goToLoginAriaLabel}>
                        <ResendLink>{copy.goToLogin}</ResendLink>
                    </AuthSwitchButton>
                ) : (
                    <Link href={loginHref}>
                        <ResendLink>{copy.goToLogin}</ResendLink>
                    </Link>
                )}
                <Button
                    size="medium"
                    gradient="purple-indigo"
                    loading={submitting}
                    disabled={code.length < 4}
                    onClick={submitCode}
                >
                    {copy.submit}
                </Button>
            </Inner>
        </Container>
    )
}

export default EmailValidation
